import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import classNames from "classnames";

import { Menu, Preloader, TopNav } from "@/components";
import { useGetUserMutation } from "@/store/usersApi";
import { useGetExercizesMutation, useEditTrainingDateMutation, useCreateTrainingMutation } from "@/store/trainingsApi";
import { tg } from "@/constants/telegram";
import { Training, SelectSample } from "@/entities/user";
import { ExerciseAlternativesModal } from "@/features/exercise-alternatives-modal";
import { ChangeDateModal } from "@/features/change-date-modal";
import { formatToYMD } from "@/shared/utils";
import toast from "react-hot-toast";

import styles from "./WorkoutPreviewPage.module.scss";
import { ReactComponent as TimeIcon } from "@/assets/img/train-cards/time-icon.svg";
import startIcon from "@/assets/img/start-icon.png";

type ExerciseItem = {
  id: number;
  title: string;
  title_photo: string;
  duration?: number;
  description?: string;
  repetitions?: number;
  sets?: number;
};

type WorkoutSection = {
  id: number;
  title: string;
  exercises: ExerciseItem[];
  isHidden?: boolean;
};

export const WorkoutPreviewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [initData, setInitData] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [training, setTraining] = useState<Training | null>(null);
  const [sections, setSections] = useState<WorkoutSection[]>([]);
  const [trainingType, setTrainingType] = useState<'free' | 'schedule'>('schedule');
  const [isUserTraining, setIsUserTraining] = useState(false);

  const [createUser] = useGetUserMutation();
  const [getExercizes] = useGetExercizesMutation();
  const [editTrainingDate, { isLoading: isMovingTraining }] = useEditTrainingDateMutation();
  const [createTraining] = useCreateTrainingMutation();

  // States for modals
  const [isAlternativesModalOpen, setIsAlternativesModalOpen] = useState(false);
  const [selectedExerciseId, setSelectedExerciseId] = useState<number | null>(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [trainingDate, setTrainingDate] = useState<string | null>(null);

  useEffect(() => {
    tg.ready();
    window.scrollTo(0, 0);
    setInitData(tg.initData || null);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        await createUser().unwrap();

        // Получаем список тренировок для определения типа
        const trainingResponse = await createTraining().unwrap();
        const trainingsData = trainingResponse.trainings || {};
        const parsedId = parseInt(id || "0");
        
        // Определяем, является ли тренировка пользовательской
        const userTraining = trainingsData.created_by_user?.find(
          (train: any) => train.id === parsedId
        );
        const isUser = !!userTraining;
        setIsUserTraining(isUser);
        
        // Для всех тренировок используем training_type: "schedule"
        const type = "schedule";
        setTrainingType(type);
        
        // Получаем дату тренировки из query параметра (если есть)
        const dateParam = searchParams.get('date');
        if (dateParam) {
          setTrainingDate(dateParam);
        }

        // Получаем упражнения тренировки
        const exercizesResponse = await getExercizes({
          training_id: Number(id),
          training_type: type,
        }).unwrap();

        // Группируем упражнения по секциям (muscle_group) или создаем одну секцию
        const exercizes = exercizesResponse.exercizes || [];
        
        // Пытаемся получить дату тренировки из первого упражнения, если она не была передана в параметрах
        if (!dateParam && exercizes.length > 0 && exercizes[0].of_date) {
          setTrainingDate(exercizes[0].of_date);
        }
        
        // Группируем по muscle_group, если есть
        const groupedByMuscle: Record<string, ExerciseItem[]> = {};
        exercizes.forEach((ex) => {
          const group = ex.muscle_group || 'Основные упражнения';
          if (!groupedByMuscle[group]) {
            groupedByMuscle[group] = [];
          }
          groupedByMuscle[group].push({
            id: ex.id,
            title: ex.title || '',
            title_photo: ex.title_photo || '',
            duration: ex.config?.[0]?.repetition_margin 
              ? ex.config[0].repetition_margin * (ex.config[0].repetitions || 1)
              : undefined,
            repetitions: ex.config?.[0]?.repetitions,
            sets: ex.config?.length || 1,
            description: ex.description,
          });
        });

        const sectionsData: WorkoutSection[] = Object.entries(groupedByMuscle).map(
          ([groupName, exercises], index) => ({
            id: index + 1,
            title: groupName,
            isHidden: false,
            exercises,
          })
        );

        if (sectionsData.length === 0 && exercizes.length > 0) {
          sectionsData.push({
            id: 1,
            title: 'Упражнения',
            isHidden: false,
            exercises: exercizes.map((ex) => ({
              id: ex.id,
              title: ex.title || '',
              title_photo: ex.title_photo || '',
              duration: ex.config?.[0]?.repetition_margin 
                ? ex.config[0].repetition_margin * (ex.config[0].repetitions || 1)
                : undefined,
              repetitions: ex.config?.[0]?.repetitions,
              sets: ex.config?.length || 1,
              description: ex.description,
            })),
          });
        }

        const exercisesData = exercizes.map((ex) => ({
          exercize_id: ex.id,
          title: ex.title || '',
          description: ex.description || null,
          title_photo: ex.title_photo || '',
          weight: ex.config?.[0]?.weight?.toString() || null,
          repetitions: ex.config?.[0]?.repetitions || 0,
          approaches: ex.config?.length || 1,
          duration: ex.config?.[0]?.repetition_margin 
            ? ex.config[0].repetition_margin * (ex.config[0].repetitions || 1)
            : 60,
          break_duration: 60, // можно получить из API, если доступно
          load_cns: 0, // можно получить из API, если доступно
          muscle_group: ex.muscle_group || '',
        }));

        // Создаем объект тренировки (базовая информация)
        const trainingData: Training = {
          training_id: Number(id),
          title: exercizesResponse.training_title || `Тренировка #${id}`,
          title_photo: exercizes[0]?.title_photo || '',
          training_duration: exercizes.reduce((acc, ex) => {
            const duration = ex.config?.[0]?.repetition_margin 
              ? ex.config[0].repetition_margin * (ex.config[0].repetitions || 1)
              : 60;
            return acc + duration;
          }, 0) / 60, // примерная длительность в минутах
          exercizes: exercisesData,
        };

        setTraining(trainingData);
        setSections(sectionsData);

        // Поддержка параметра openFirstTrain для открытия тренировки в Telegram боте
        const openFirstTrain = searchParams.get("openFirstTrain");
        if (openFirstTrain === "true" && exercizes.length > 0) {
          const firstExerciseId = exercizes[0].id;
          const trainingId = Number(id);
          const tgLink = `https://t.me/FitGuid_bot?start=exercize-${trainingId}-${firstExerciseId}`;
          window.location.href = tgLink;
          tg.close();
        }
      } catch (err) {
        console.error("Ошибка при загрузке данных:", err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, createUser, getExercizes, searchParams]);

  const toggleSection = (sectionId: number) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? { ...section, isHidden: !section.isHidden }
          : section
      )
    );
  };

  const handleStartWorkout = () => {
    // Переход к первому упражнению
    if (sections.length > 0 && sections[0].exercises.length > 0) {
      const firstExercise = sections[0].exercises[0];
      // Для всех тренировок используем training_type: "schedule"
      navigate(`/workout-exercise/${id}/${firstExercise.id}?type=schedule`);
    }
  };

  const handleAskQuestion = () => {
    if (!id) return;
    const tgLink = `https://t.me/FitGuid_bot?start=questionTraining-${id}`;
    window.location.href = tgLink;
    tg.close();
  };

  const handleOpenAlternatives = (exerciseId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to exercise page
    setSelectedExerciseId(exerciseId);
    setIsAlternativesModalOpen(true);
  };

  const handleExerciseReplaced = async () => {
    // Reload exercises after replacement
    if (!id) return;
    
    try {
      // Для всех тренировок используем training_type: "schedule"
      const exercizesResponse = await getExercizes({
        training_id: Number(id),
        training_type: "schedule",
      }).unwrap();

      const exercizes = exercizesResponse.exercizes || [];
      
      // Regroup exercises
      const groupedByMuscle: Record<string, ExerciseItem[]> = {};
      exercizes.forEach((ex) => {
        const group = ex.muscle_group || 'Основные упражнения';
        if (!groupedByMuscle[group]) {
          groupedByMuscle[group] = [];
        }
        groupedByMuscle[group].push({
          id: ex.id,
          title: ex.title || '',
          title_photo: ex.title_photo || '',
          duration: ex.config?.[0]?.repetition_margin 
            ? ex.config[0].repetition_margin * (ex.config[0].repetitions || 1)
            : undefined,
          repetitions: ex.config?.[0]?.repetitions,
          sets: ex.config?.length || 1,
          description: ex.description,
        });
      });

      const sectionsData: WorkoutSection[] = Object.entries(groupedByMuscle).map(
        ([groupName, exercises], index) => ({
          id: index + 1,
          title: groupName,
          isHidden: false,
          exercises,
        })
      );

      if (sectionsData.length === 0 && exercizes.length > 0) {
        sectionsData.push({
          id: 1,
          title: 'Упражнения',
          isHidden: false,
          exercises: exercizes.map((ex) => ({
            id: ex.id,
            title: ex.title || '',
            title_photo: ex.title_photo || '',
            duration: ex.config?.[0]?.repetition_margin 
              ? ex.config[0].repetition_margin * (ex.config[0].repetitions || 1)
              : undefined,
            repetitions: ex.config?.[0]?.repetitions,
            sets: ex.config?.length || 1,
            description: ex.description,
          })),
        });
      }

      setSections(sectionsData);
      toast.success("Упражнение успешно заменено");
    } catch (error) {
      console.error("Error reloading exercises:", error);
      toast.error("Ошибка при обновлении упражнений");
    }
  };

  const handleOpenMoveModal = () => {
    if (!trainingDate) {
      toast.error("Дата тренировки не указана");
      return;
    }
    setSelectedDate(new Date(trainingDate));
    setIsMoveModalOpen(true);
  };

  const handleMoveTraining = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id || !selectedDate || !trainingDate) return;

    try {
      const oldFormattedDate = formatToYMD(trainingDate);
      const newFormattedDate = formatToYMD(selectedDate);

      await editTrainingDate({
        trainig_id: Number(id),
        old_date: oldFormattedDate,
        new_date: newFormattedDate,
      }).unwrap();

      toast.success("Тренировка успешно перенесена");
      setIsMoveModalOpen(false);
      // Optionally reload the page or update the date
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Error moving training:", error);
      toast.error("Ошибка при переносе тренировки");
    }
  };

  if (isLoading) {
    return (
      <>
        <Preloader hasMenu />
        <Menu active={0} />
      </>
    );
  }

  if (error || !training) {
    return (
      <div>
        <p>Ошибка загрузки тренировки</p>
        <Menu active={0} />
      </div>
    );
  }
  

  return (
    <div className={styles.root}>
      <div className="container" style={{ position: "relative" }}>
        <TopNav
          linkPath="/trains"
          handleSetActivePage={() => null}
          title="Просмотр тренировки"
        />

        {/* Workout Header Banner */}
        <div className={styles.workoutBanner}>
          <h2 className={styles.workoutTitle}>{training.title}</h2>
        </div>

        {/* Action Buttons */}
        <div className={styles.actionButtons}>
          {/* Кнопка редактирования для пользовательских тренировок */}
          {isUserTraining && (
            <button 
              className={styles.editButton} 
              onClick={() => navigate(`/edit-training/${id}`)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M14.1667 2.5L17.5 5.83333M15 1.66667L18.3333 5L10.8333 12.5H7.5V9.16667L15 1.66667Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Редактировать тренировку</span>
            </button>
          )}
          {/* Скрываем кнопку "Задать вопрос" для тренировок, созданных пользователем */}
          {!isUserTraining && (
            <button className={styles.questionButton} onClick={handleAskQuestion}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.5 7.5C7.5 6.67157 8.17157 6 9 6H10C10.8284 6 11.5 6.67157 11.5 7.5V8.5C11.5 9.32843 10.8284 10 10 10H9.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.5 13H10.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Задать вопрос по тренировке</span>
            </button>
          )}
          
          {trainingDate && (
            <button className={styles.moveButton} onClick={handleOpenMoveModal}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M15.8333 2.5H4.16667C3.24619 2.5 2.5 3.24619 2.5 4.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V4.16667C17.5 3.24619 16.7538 2.5 15.8333 2.5Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.3333 1.66667V5.00001"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66667 1.66667V5.00001"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2.5 8.33333H17.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Перенести тренировку</span>
            </button>
          )}
        </div>

        {/* Duration Badge */}
        <div className={styles.durationBadge}>
          <TimeIcon />
          <span>{Math.floor(Number(training.training_duration || 0))} мин</span>
        </div>

        {/* Workout Sections */}
        {sections.map((section) => (
          <div key={section.id} className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>{section.title}</h3>
              <button
                className={styles.toggleButton}
                onClick={() => toggleSection(section.id)}
              >
                {section.isHidden ? "Показать" : "Скрыть"}
                <span className={classNames(styles.toggleIcon, {
                  [styles.toggleIconUp]: !section.isHidden,
                  [styles.toggleIconDown]: section.isHidden,
                })}>▲</span>
              </button>
            </div>

            {!section.isHidden && (
              <div className={styles.exerciseList}>
                {section.exercises.map((exercise) => (
                  <div 
                    key={exercise.id} 
                    className={styles.exerciseItem}
                    onClick={() => {
                      // Для всех тренировок используем training_type: "schedule"
                      navigate(`/workout-exercise/${id}/${exercise.id}?type=schedule`);
                    }}
                  >
                    <div className={styles.exerciseContent}>
                      <div className={styles.exerciseThumbnail}>
                        <img src={exercise.title_photo} alt={exercise.title} />
                      </div>
                      <div className={styles.exerciseInfo}>
                        <h4 className={styles.exerciseTitle}>{exercise.title}</h4>
                        <p className={styles.exerciseDuration}>
                          {exercise.duration
                            ? `${exercise.duration} sec`
                            : `${exercise.repetitions} reps`}
                        </p>
                        {exercise.sets && (
                          <div className={styles.setsContainer}>
                            <span className={styles.setsLabel}>{exercise.sets} Sets:</span>
                            <div className={styles.setsIndicators}>
                              {Array.from({ length: exercise.sets }).map((_, index) => (
                                <div key={index} className={styles.setIndicator} />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        className={styles.replaceButton}
                        onClick={(e) => handleOpenAlternatives(exercise.id, e)}
                        title="Заменить упражнение"
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path
                            d="M5.25 3.75L2.25 6.75L5.25 9.75"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12.75 14.25L15.75 11.25L12.75 8.25"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M2.25 6.75H11.25C13.3211 6.75 15 8.42893 15 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M15.75 11.25H6.75C4.67893 11.25 3 9.57107 3 7.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <div className={styles.exerciseArrow}>
                        <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                          <path
                            d="M1 1L7 7L1 13"
                            stroke="#C7C7CC"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Lightning Mode */}
        <div className={styles.lightningMode}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M9 1L3 9H8L7 15L13 7H8L9 1Z"
              fill="#FFCC00"
            />
          </svg>
          <span>Начать тренировку в быстром режиме</span>
        </div>
      </div>

      {/* Start Button - Fixed */}
      <div className={styles.startButtonContainer}>
        <button className={styles.startButton} onClick={handleStartWorkout}>
          <div className={styles.playIcon}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path
                d="M11 8L11 28L26 18L11 8Z"
                fill="white"
                stroke="white"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span className={styles.startButtonText}>Start</span>
        </button>
      </div>

      {/* Modals */}
      {selectedExerciseId && id && (
        <ExerciseAlternativesModal
          isOpen={isAlternativesModalOpen}
          onClose={() => {
            setIsAlternativesModalOpen(false);
            setSelectedExerciseId(null);
          }}
          exerciseId={selectedExerciseId}
          trainingId={Number(id)}
          onExerciseReplaced={handleExerciseReplaced}
        />
      )}

      {isMoveModalOpen && (
        <ChangeDateModal
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onClose={() => setIsMoveModalOpen(false)}
          isLoading={isMovingTraining}
          onSubmit={handleMoveTraining}
        />
      )}

      <Menu active={0} />
    </div>
  );
};


